<template>
    <div class="bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />
        <h2 class="text-xl font-semibold mb-4 text-gray-800">
            Invoice SPK Subcon Print
        </h2>

        <form @submit.prevent="generateAndDisplayPdf" class="flex items-end gap-4">
            <div class="w-7/12">
                <label class="block text-gray-700 text-sm font-bold mb-2" for="invoiceNumber">
                    Invoice SPK #:
                </label>
                <select
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="invoiceNumber" v-model="formData.invoiceNumber" required>
                    <option value="">-- Pilih Invoice SPK # --</option>
                    <option v-for="invoice in invoiceOptions" :key="invoice.id" :value="invoice.id">
                        {{ invoice.invoiceNum }}
                    </option>
                </select>
            </div>
            <div class="w-3/12">
                <label class="block text-gray-700 text-sm font-bold mb-2" for="invoiceDate">
                    Invoice Date:
                </label>
                <input
                    class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    type="date" id="invoiceDate" v-model="formData.invoiceDate" disabled />
            </div>
            <div class="w-2/12">
                <button type="submit"
                    class="bg-blue-600 hover:bg-blue-800 text-white font-bold py-2 px-6 rounded focus:outline-none focus:shadow-outline w-full">
                    Preview
                </button>
            </div>
        </form>

        <div v-if="pdfUrl" class="mt-8 border-t-2 pt-6">
            <h3 class="text-xl font-semibold mb-4 text-gray-800">
                Print Preview
            </h3>
            <iframe :src="pdfUrl" class="w-full h-screen border rounded-md"></iframe>
        </div>

    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import gspeLogo from '@/assets/images/gspe.png';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

export default {
    components: {
        MonkeyLoader
    },
    data() {
        return {
            formData: {
                invoiceNumber: '',
                invoiceDate: this.getTodayDate(),
            },
            invoiceOptions: [],
            preparedData: null,
            pdfUrl: null,
        };
    },
    methods: {
        // ... (metode lain tidak berubah) ...
        getTodayDate() {
            return new Date().toISOString().split('T')[0];
        },
        async loadInvoiceOptions() {
            try {
                const response = await axios.get(`${window.location.origin}/api/invoicesubcon`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });
                this.invoiceOptions = response.data.map(inv => ({
                    id: inv.id,
                    invoiceNum: inv.invnum,
                }));
            } catch (error) {
                console.error('Error fetching Invoice options:', error);
                this.invoiceOptions = [];
                Swal.fire('Error', 'Gagal memuat daftar invoice dari API.', 'error');
            }
        },
        async prepareInvoiceData(invoiceId) {
            if (!invoiceId) {
                this.preparedData = null;
                this.formData.invoiceDate = this.getTodayDate();
                return;
            }
            try {
                const response = await axios.get(`${window.location.origin}/api/invoicesubconprint/${invoiceId}`, {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                });

                const data = response.data;
                const totalHarga = data.details.reduce((sum, item) => sum + item.invoicePrice, 0);

                this.formData.invoiceDate = data.invDate ? data.invDate.substring(0, 10) : this.getTodayDate();

                this.preparedData = {
                    noInvoice: data.invNum,
                    tglInvoice: data.invDate,
                    pekerjaanOleh: data.entityName,
                    noRek: data.contactRek,
                    namaRek: data.contactName,
                    bank: data.bankName,
                    npwp: data.contactNpwp,
                    caraPembayaran: "Transfer",
                    details: data.details,
                    totalHarga: totalHarga,
                };

            } catch (error) {
                console.error(`Error fetching invoice print detail for ID ${invoiceId}:`, error);
                this.preparedData = null;
                Swal.fire('Error', 'Gagal memuat detail invoice dari API.', 'error');
            }
        },

        generateAndDisplayPdf() {
            if (!this.preparedData) {
                Swal.fire({ icon: 'error', title: 'Data Belum Siap', text: 'Data untuk invoice ini belum selesai dimuat.' });
                return;
            }

            const doc = new jsPDF('p', 'mm', 'a4');
            const margin = 15;
            let cursorY = margin;
            const data = this.preparedData;

            doc.addImage(gspeLogo, 'PNG', margin, cursorY, 40, 15);
            cursorY += 22; // Jarak disesuaikan

            doc.setFont('times', 'normal');
            doc.setFontSize(11);
            doc.text('Berikut adalah ketentuan Harga untuk Instalasi dan ATP', margin, cursorY);
            cursorY += 8; // Jarak disesuaikan

            const tableHead = [['SPK', 'Site', 'Area', 'Project', 'Price']];
            const tableBody = data.details.map(item => [
                item.spkNum,
                item.sitename,
                item.area,
                item.projectName,
                `Rp. ${item.invoicePrice.toLocaleString('id-ID')}`
            ]);

            // --- PERUBAHAN 1: Mengubah footer tabel menggunakan `colSpan` ---
            const tableFoot = [
                [
                    {
                        content: 'TOTAL HARGA',
                        colSpan: 4, // Gabungkan 4 kolom pertama
                        styles: { halign: 'left', fillColor: [220, 220, 220], fontStyle: 'bold', textColor: 0 }
                    },
                    {
                        content: `Rp. ${data.totalHarga.toLocaleString('id-ID')}`,
                        styles: { halign: 'right', fillColor: [220, 220, 220], fontStyle: 'bold', textColor: 0 }
                    }
                ]
            ];

            autoTable(doc, {
                head: tableHead,
                body: tableBody,
                foot: tableFoot, // Menggunakan footer baru
                startY: cursorY,
                theme: 'grid',
                styles: { font: "times", fontSize: 8, cellPadding: 2, lineColor: [0, 0, 0], lineWidth: 0.1 },
                headStyles: { font: "times", fillColor: [220, 220, 220], textColor: 0, fontStyle: 'bold', halign: 'center' },
                // footStyles dihapus karena style sudah didefinisikan di sel footer langsung
            });
            cursorY = doc.lastAutoTable.finalY + 8; // Jarak disesuaikan

            const infoBody = [
                ['Pekerjaan dilakukan oleh', `: ${data.pekerjaanOleh}`],
                ['No.Rek', `: ${data.noRek}`],
                ['Nama Rekening', `: ${data.namaRek}`],
                ['Bank', `: ${data.bank}`],
                ['NPWP', `: ${data.npwp}`],
                ['Alamat NPWP', ': -'],
                ['', ''],
                ['Cara Pembayaran', `: ${data.caraPembayaran}`],
                ['Tanggal Invoice', `: ${this.formatDate(data.tglInvoice)}`],
                ['No. Invoice', `: ${data.noInvoice}`],
            ];

            autoTable(doc, {
                body: infoBody,
                startY: cursorY,
                theme: 'plain',
                styles: { font: "times", fontSize: 9, cellPadding: 1 },
                columnStyles: {
                    0: { cellWidth: 45, fontStyle: 'bold' },
                    1: { cellWidth: 'auto' }
                },
            });
            cursorY = doc.lastAutoTable.finalY + 8; // Jarak disesuaikan

            doc.setFont('times', 'normal');
            doc.setFontSize(10);
            doc.text('Harga untuk pekerjaan di atas sudah, dan sepengetahuan dari :', margin, cursorY);
            cursorY += 30; // Jarak disesuaikan

            const signatureXPositions = [margin, margin + 45, margin + 90, margin + 135];
            doc.setFontSize(9);
            doc.text('(Rizky A)', signatureXPositions[0], cursorY);
            doc.text('(Masrul)', signatureXPositions[1], cursorY);
            doc.text('(Susy Verawaty)', signatureXPositions[2], cursorY);
            doc.text('(Muh. Muslimin)', signatureXPositions[3], cursorY);

            this.pdfUrl = doc.output('datauristring');
        },
        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            // Langsung format, browser akan otomatis menyesuaikan ke timezone pengguna
            return date.toLocaleDateString('id-ID', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            });
        },
    },
    mounted() {
        this.loadInvoiceOptions();
    },
    watch: {
        'formData.invoiceNumber'(newInvoiceNumber) {
            this.pdfUrl = null;
            this.prepareInvoiceData(newInvoiceNumber);
        },
    },
};
</script>

<style scoped>
.w-full {
    width: 100%;
}

.h-screen {
    height: 100vh;
}

.input-border {
    border-bottom: 2px solid #ccc;
    border-radius: 0;
    border-left: none;
    border-right: none;
    border-top: none;
}

.input-border:focus {
    border-bottom-color: #007bff;
    box-shadow: none;
}
</style>